import { useMemo, useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Form, Row, Col, Input, Label, Button, FormGroup, Collapse } from "reactstrap";
import Swal from 'sweetalert2';

const inspectionTypeOptions = [
    { value: 'all', label: 'All Inspections' },
    { value: 'move_in', label: 'Move-In' },
    { value: 'move_out', label: 'Move-Out' },
];

const PropertySelector = ({
    propertyList,
    selectedFilters,
    onFiltersChange,
    onAddClick,
    isLoading,
    showUnitFilter = false,
    user,
}) => {
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const currentProperty = useMemo(
        () => propertyList.find((prop) => prop._id === selectedFilters.property_id),
        [propertyList, selectedFilters.property_id]
    );

    const buildingList = useMemo(() => {
        if (!currentProperty?.building) return [];
        return currentProperty.building
            .filter((b) => b && b.buildingName)
            .map((b) => b.buildingName);
    }, [currentProperty]);

    const unitList = useMemo(() => {
        if (!currentProperty?.building || !selectedFilters.building_name) return [];
        const selectedBuilding = currentProperty.building.find(
            (b) => b.buildingName === selectedFilters.building_name
        );
        if (!selectedBuilding?.units) return [];
        return selectedBuilding.units
            .filter((u) => u && u.unit_no)
            .map((u) => u.unit_no)
            .sort();
    }, [currentProperty, selectedFilters.building_name]);

    const isAddButtonDisabled = useMemo(() => {
        if (!selectedFilters.property_id) return true;
        if (buildingList.length > 0 && !selectedFilters.building_name) return true;
        if (showUnitFilter && !selectedFilters.unit_number?.trim()) return true;
        return false;
    }, [
        selectedFilters.property_id,
        selectedFilters.building_name,
        selectedFilters.unit_number,
        buildingList.length,
        showUnitFilter,
    ]);

    // ── Options ────────────────────────────────────────────────────────────────

    const propertyOptions = useMemo(
        () =>
            propertyList.map((prop) => ({
                value: prop._id,
                label: `${prop.title}${prop.location || prop.city ? `, ${prop.location || prop.city}` : ''}`,
            })),
        [propertyList]
    );

    const buildingOptions = useMemo(
        () => buildingList.map((name) => ({ value: name, label: name })),
        [buildingList]
    );

    const selectedPropertyOption = useMemo(
        () => propertyOptions.find((opt) => opt.value === selectedFilters.property_id) || null,
        [propertyOptions, selectedFilters.property_id]
    );

    const selectedBuildingOption = useMemo(
        () => buildingOptions.find((opt) => opt.value === selectedFilters.building_name) || null,
        [buildingOptions, selectedFilters.building_name]
    );

    // ── Auto-select single options ─────────────────────────────────────────────

    useEffect(() => {
        if (buildingList.length === 1 && !selectedFilters.building_name && selectedFilters.property_id) {
            onFiltersChange({
                ...selectedFilters,
                building_name: buildingList[0],
            });
        }
    }, [buildingList, selectedFilters, onFiltersChange]);

    useEffect(() => {
        if (
            showUnitFilter &&
            unitList.length === 1 &&
            !selectedFilters.unit_number &&
            selectedFilters.building_name
        ) {
            onFiltersChange({
                ...selectedFilters,
                unit_number: unitList[0],
            });
        }
    }, [unitList, selectedFilters, onFiltersChange, showUnitFilter]);

    // ── Handlers ───────────────────────────────────────────────────────────────

    const handlePropertyChange = useCallback(
        (selectedOption) => {
            const newPropertyId = selectedOption ? selectedOption.value : '';
            const selectedProp = propertyList.find((p) => p._id === newPropertyId);

            onFiltersChange({
                property_id: newPropertyId,
                building_name: '',
                unit_number: '',
                address: selectedProp?.address || '',
                postal_code: selectedProp?.postal_code || '',
            });
        },
        [propertyList, onFiltersChange]
    );

    const handleBuildingChange = useCallback(
        (selectedOption) => {
            onFiltersChange({
                building_name: selectedOption ? selectedOption.value : '',
                unit_number: '',
            });
        },
        [onFiltersChange]
    );

    const handleUnitChange = useCallback(
        (e) => {
            onFiltersChange({ unit_number: e.target.value });
        },
        [onFiltersChange]
    );

    const handleTypeChange = useCallback(
        (selectedOption) => {
            onFiltersChange({
                inspection_type: selectedOption ? selectedOption.value : 'all',
            });
        },
        [onFiltersChange]
    );

    const handleDateChange = useCallback(
        (field) => (e) => {
            onFiltersChange({ [field]: e.target.value || '' });
        },
        [onFiltersChange]
    );

    const handleAddClickWithValidation = useCallback(() => {
        if (showUnitFilter && !selectedFilters.unit_number?.trim()) {
            Swal.fire({
                title: 'Suite Number Required',
                text: 'Please enter the Suite Number before generating the inspection.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        onAddClick();
    }, [showUnitFilter, selectedFilters.unit_number, onAddClick]);

    const toggleFilters = () => setIsFiltersOpen((prev) => !prev);

    const customSelectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        control: (base, state) => ({
            ...base,
            zIndex: 2,
            backgroundColor:
                (buildingList.length === 1 && !showUnitFilter) ||
                    (unitList.length === 1 && showUnitFilter)
                    ? '#f8f9fa'
                    : base.backgroundColor,
            cursor:
                (buildingList.length === 1 && !showUnitFilter) ||
                    (unitList.length === 1 && showUnitFilter)
                    ? 'not-allowed'
                    : 'default',
        }),
    };

    return (
        <Form className="mb-4">
            {/* Main row */}
            <Row className="g-3 align-items-end">

                {/* Property */}
                <Col xs={12} md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Property</Label>
                        <Select
                            value={selectedPropertyOption}
                            onChange={handlePropertyChange}
                            options={propertyOptions}
                            placeholder="Select Property"
                            isClearable
                            isDisabled={isLoading}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </FormGroup>
                </Col>

                {/* Building */}
                {buildingList.length > 0 && (
                    <Col xs={12} md={3}>
                        <FormGroup>
                            <Label className="fw-semibold">Building</Label>
                            <Select
                                value={selectedBuildingOption}
                                onChange={handleBuildingChange}
                                options={buildingOptions}
                                placeholder="Select Building"
                                isClearable={buildingList.length > 1}
                                isDisabled={buildingList.length === 1 || isLoading}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </FormGroup>
                    </Col>
                )}

                {/* Unit (if enabled) */}
                {showUnitFilter && (
                    <Col xs={12} md={2}>
                        <FormGroup>
                            <Label className="fw-semibold">Unit / Suite</Label>
                            <Input
                                type="text"
                                placeholder="e.g. 201, #5, Basement"
                                value={selectedFilters.unit_number || ''}
                                onChange={handleUnitChange}
                                disabled={isLoading}
                            />
                        </FormGroup>
                    </Col>
                )}

                {/* Buttons column */}
                <Col md={buildingList.length > 0 ? 4 : 7}>
                    <div
                        className="d-flex gap-2 align-items-center justify-content-md-end flex-wrap"
                        style={{ height: '100%', minHeight: '38px' }}
                    >
                        <Button
                            color="primary"
                            onClick={handleAddClickWithValidation}
                            disabled={isAddButtonDisabled || isLoading}
                            className="px-3"
                            style={{ minWidth: '140px' }}
                        >
                            <FeatherIcon icon="plus-circle" size={18} className="me-2" />
                            Generate
                        </Button>

                        <Button
                            color="primary"
                            outline
                            onClick={toggleFilters}
                            disabled={isLoading}
                            className="px-3"
                            style={{ minWidth: '120px' }}
                        >
                            <FeatherIcon icon="filter" size={18} className="me-2" />
                            Filters
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Collapsible Filters Section */}
            <Collapse isOpen={isFiltersOpen}>
                <Row className="g-3 mt-4">
                    {/* Inspection Type */}
                    <Col xs={12} md={4}>
                        <FormGroup>
                            <Label className="fw-semibold">Inspection Type</Label>
                            <Select
                                value={inspectionTypeOptions.find((o) => o.value === selectedFilters.inspection_type) || null}
                                onChange={handleTypeChange}
                                options={inspectionTypeOptions}
                                isDisabled={isLoading}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </FormGroup>
                    </Col>

                    {/* Date Range */}
                    <Col xs={12} md={8}>
                        <FormGroup>
                            <Label className="fw-semibold">Date Range</Label>
                            <Row className="g-2">
                                <Col xs={6}>
                                    <Input
                                        type="date"
                                        value={selectedFilters.date_from || ''}
                                        onChange={handleDateChange('date_from')}
                                        disabled={isLoading}
                                        placeholder="From"
                                    />
                                </Col>
                                <Col xs={6}>
                                    <Input
                                        type="date"
                                        value={selectedFilters.date_to || ''}
                                        onChange={handleDateChange('date_to')}
                                        min={selectedFilters.date_from || undefined}
                                        disabled={isLoading}
                                        placeholder="To"
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
            </Collapse>
        </Form>
    );
};

export { PropertySelector };